import { useEffect, useState } from "react";
import "./AdminHandleFacility.scss";
import { combinedQuery } from "../../api/combinedQueries/combinedQueries";
import {
  IFacility,
  ISeasonalHoursFacility,
  ISpecialHoursFacility,
} from "../../types/facility";
import { getWeekdaySwedishName } from "../../shared/utility";
import Spinner from "../../components/UI/Spinner/Spinner";
import { toast } from "react-toastify";
import CreateDefaultHoursPopup from "../../containers/Facility/CreateDefaultHoursPopup/CreateDefaultHoursPopup";
import EditDefaultHoursPopup from "../../containers/Facility/EditDefaultHoursPopup/EditDefaultHoursPopup";
import { IWeekdayHoursFacility } from "../../types/facility";
import CreateSeasonalHoursPopup from "../../containers/Facility/CreateSeasonalHoursPopup/CreateSeasonalHoursPopup";
import EditSeasonalHoursPopup from "../../containers/Facility/EditSeasonalHoursPopup/EditSeasonalHoursPopup";
import CreateSpecialHoursPopup from "../../containers/Facility/CreateSpecialHoursPopup/CreateSpecialHoursPopup";
import EditSpecialHoursPopup from "../../containers/Facility/EditSpecialHoursPopup/EditSpecialHoursPopup";
import Dropdown from "../../components/UI/Dropdown/Dropdown";
import {
  COURT_LABELS,
  COURT_TYPES,
  FacilityByCourt,
  FacilityCourtType,
  IMergedRow,
  mergeDefaults,
  mergeSeasonals,
  mergeSpecials,
  shortDate,
  shortRange,
  shortTime,
  summariseWeekdayHours,
} from "./mergeHours";

interface IAdminHandleFacilityProps {}

/**
 * The court types a row covers, plus the ones it conspicuously doesn't.
 * Always rendered in Inne / Ute / Rehab order, present or not, so a given
 * court sits in the same place on every row and a gap reads as a gap rather
 * than as a shorter list.
 */
function CourtBadges({ row }: { row: IMergedRow<any> }) {
  return (
    <span className="court-badges">
      {COURT_TYPES.map((type) => {
        if (row.courtTypes.includes(type)) {
          return (
            <span key={type} className={`court-badge ${type}`}>
              {COURT_LABELS[type]}
            </span>
          );
        }
        if (row.missing.includes(type)) {
          // The dashed outline carries the meaning; spelling out "saknas"
          // just makes the column wider.
          return (
            <span
              key={type}
              className="court-badge missing"
              title={`${COURT_LABELS[type]} saknas`}
            >
              {COURT_LABELS[type]}
            </span>
          );
        }
        // Not loaded at all — say nothing rather than claim it is missing.
        return null;
      })}
    </span>
  );
}

function AdminHandleFacility(props: IAdminHandleFacilityProps) {
  const [loading, setLoading] = useState<boolean>(false);

  const [facility, setFacility] = useState<IFacility>();

  // Every list shows all court types at once, so all three are loaded.
  // `facility` above is kept only as a "has anything loaded yet" signal.
  const [facilityByCourt, setFacilityByCourt] = useState<FacilityByCourt>({});

  const [selectedSeasonalRow, setSelectedSeasonalRow] =
    useState<IMergedRow<ISeasonalHoursFacility>>();
  const [selectedSpecialRow, setSelectedSpecialRow] =
    useState<IMergedRow<ISpecialHoursFacility>>();

  const [currentSelectedDefaultHour, setCurrentSelectedDefaultHour] =
    useState<IWeekdayHoursFacility>();
  const [selectedDefaultCourtTypes, setSelectedDefaultCourtTypes] = useState<
    FacilityCourtType[]
  >([]);

  const [showCreateDefaultHoursPopup, setShowCreateDefaultHoursPopup] =
    useState(false);
  const [showEditDefaultHoursPopup, setShowEditDefaultHoursPopup] =
    useState(false);
  const [showCreateSeasonalHoursPopup, setShowCreateSeasonalHoursPopup] =
    useState(false);
  const [showEditSeasonalHoursPopup, setShowEditSeasonalHoursPopup] =
    useState(false);
  const [showCreateSpecialHoursPopup, setShowCreateSpecialHoursPopup] =
    useState(false);
  const [showUpdateSpecialHoursPopup, setShowUpdateSpecialHoursPopup] =
    useState(false);

  // Every seasonal/special row ever created is returned, so the lists grow
  // without bound. Filter them by year, defaulting to the current one.
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );

  const yearOf = (date?: string) =>
    date && date.length >= 4 ? Number(date.slice(0, 4)) : NaN;

  // Years present in the data, plus the current and next year so a new season
  // can always be filed forward.
  const availableYears = (() => {
    const currentYear = new Date().getFullYear();
    const years = new Set<number>([currentYear, currentYear + 1]);
    COURT_TYPES.forEach((courtType) => {
      facilityByCourt[courtType]?.seasonal_hours?.forEach((s) => {
        [yearOf(s.start_date), yearOf(s.end_date)].forEach((y) => {
          if (!isNaN(y)) years.add(y);
        });
      });
      facilityByCourt[courtType]?.special_days?.forEach((d) => {
        const y = yearOf(d.date);
        if (!isNaN(y)) years.add(y);
      });
    });
    return Array.from(years).sort((a, b) => a - b);
  })();

  // Seasons span a range and some cross new year (Vinterstängt runs
  // 2025-10-02 to 2026-03-30), so match on range overlap rather than on the
  // start date's year — otherwise such a season vanishes from the later year.
  const seasonalRows = mergeSeasonals(facilityByCourt)
    .filter((row) => {
      const start = yearOf(row.sample.start_date);
      const end = yearOf(row.sample.end_date);
      if (isNaN(start) && isNaN(end)) return false;
      const from = isNaN(start) ? end : start;
      const to = isNaN(end) ? start : end;
      return from <= selectedYear && to >= selectedYear;
    });

  const defaultRows = mergeDefaults(facilityByCourt);

  const specialRows = mergeSpecials(facilityByCourt)
    .filter((row) => yearOf(row.sample.date) === selectedYear);

  // getFacilityOpeningHours takes a single court type, so all three are
  // fetched in parallel. The payloads are small (a few dozen rows each).
  const fetchChosenItems = async ({ fetchFacility = false }) => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        COURT_TYPES.map((courtType) =>
          combinedQuery({
            requestor: "admin",
            court_type: courtType,
            fetchFacility: fetchFacility,
          }).then((response) => ({ courtType, response })),
        ),
      );

      if (fetchFacility) {
        const next: FacilityByCourt = {};
        responses.forEach(({ courtType, response }) => {
          if (response.facility && !("message" in response.facility)) {
            next[courtType] = response.facility;
          }
        });
        setFacilityByCourt(next);
        setFacility(next.INDOOR ?? next.OUTDOOR ?? next.REHAB);
      }
      setLoading(false);
    } catch (error: any) {
      toast.error(error);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchChosenItems({
      fetchFacility: true,
    });
  }, []);

  useEffect(() => {
    if (currentSelectedDefaultHour) {
      setShowEditDefaultHoursPopup(true);
    }
    if (selectedSeasonalRow) {
      setShowEditSeasonalHoursPopup(true);
    }
    if (selectedSpecialRow) {
      setShowUpdateSpecialHoursPopup(true);
    }
  }, [currentSelectedDefaultHour, selectedSeasonalRow, selectedSpecialRow]);

  if (!facility) return <Spinner />;
  return (
    <div className="admin-handle-facility-layout">
      <div className="handle-faclity-declerations-wrapper">
        <div className="handle-facility-oversight-info-wrapper">
          <h1 className="text-l primary-text">Öppettider</h1>
        </div>
      </div>
      <h1 className="text-m">
        Utomhusbanorna visas inte för använadre mellan 22 oktober och 1 april
      </h1>
      <div className="handle-facility-actions-wrapper">
        <div className="admin-handle-facility-filters">
          <Dropdown
            classes="facility-year-select"
            width={110}
            height={35}
            bordered
            title="Välj år"
            value={String(selectedYear)}
            onSelect={(value) => {
              if (value) setSelectedYear(Number(value));
            }}
            options={availableYears.map((year) => ({
              value: String(year),
              label: String(year),
            }))}
          />
        </div>
        <div className="handle-facility-buttons">
          <button
            className="text-m"
            onClick={() => setShowCreateDefaultHoursPopup(true)}
          >
            Skapa grundinställning
          </button>
          <button
            className="text-m"
            onClick={() => setShowCreateSeasonalHoursPopup(true)}
          >
            Skapa avvikande inställning
          </button>
          <button
            className="text-m"
            onClick={() => setShowCreateSpecialHoursPopup(true)}
          >
            Skapa dagsaktuell inställning
          </button>
        </div>
      </div>
      <h1 className="text-l primary-text">Ordinarie öppettider</h1>
      <div className="hours-rows text-m">
        {defaultRows.map((row) => (
          <div
            key={row.key}
            className="hours-row pointer"
            onClick={() =>
              {
                setSelectedDefaultCourtTypes(row.courtTypes);
                setCurrentSelectedDefaultHour({
                  weekday: row.sample.weekday,
                  opening_hours: row.sample.opening_hours,
                  closing_hours: row.sample.closing_hours,
                  court_type: row.courtTypes[0],
                });
              }
            }
          >
            <span className="hours-row-date">
              {getWeekdaySwedishName(row.sample.weekday)}
            </span>
            <span className="hours-row-name"></span>
            <span className="hours-row-times secondary-text">
              {shortTime(row.sample.opening_hours)}–
              {shortTime(row.sample.closing_hours)}
            </span>
            <CourtBadges row={row} />
            <i className="fa-regular fa-pen-to-square"></i>
          </div>
        ))}
      </div>

      <h2 className="text-l primary-text">Avvikande ordinarie öppettider</h2>
      <div className="hours-rows text-m">
        {seasonalRows.length === 0 && (
          <div className="hours-row-empty secondary-text">
            Inga avvikande öppettider för {selectedYear}
          </div>
        )}
        {seasonalRows.map((row) => (
          <div
            key={row.key}
            className="hours-row pointer"
            onClick={() => setSelectedSeasonalRow(row)}
          >
            <span className="hours-row-date">
              {shortRange(row.sample.start_date, row.sample.end_date)}
            </span>
            <span className="hours-row-name">{row.sample.alias}</span>
            <span className="hours-row-times secondary-text">
              {row.sample.closed
                ? "Stängt"
                : summariseWeekdayHours(row.sample.weekday_hours)}
            </span>
            <CourtBadges row={row} />
            <i className="fa-regular fa-pen-to-square"></i>
          </div>
        ))}
      </div>

      <h2 className="text-l primary-text">Dagsaktuella öppettider</h2>
      <div className="hours-rows text-m">
        {specialRows.length === 0 && (
          <div className="hours-row-empty secondary-text">
            Inga dagsaktuella öppettider för {selectedYear}
          </div>
        )}
        {specialRows.map((row) => (
          <div
            key={row.key}
            className="hours-row pointer"
            onClick={() => setSelectedSpecialRow(row)}
          >
            <span className="hours-row-date">
              {shortDate(row.sample.date)}
            </span>
            <span className="hours-row-name">{row.sample.alias}</span>
            <span className="hours-row-times secondary-text">
              {row.sample.closed
                ? "Stängt"
                : `${shortTime(row.sample.opening_hours)}–${shortTime(
                    row.sample.closing_hours,
                  )}`}
            </span>
            <CourtBadges row={row} />
            <i className="fa-regular fa-pen-to-square"></i>
          </div>
        ))}
      </div>

      <CreateDefaultHoursPopup
        showPopup={showCreateDefaultHoursPopup}
        onClose={() => {
          setShowCreateDefaultHoursPopup(false);
          setCurrentSelectedDefaultHour(undefined);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
      />
      <EditDefaultHoursPopup
        showPopup={showEditDefaultHoursPopup}
        onClose={() => {
          setShowEditDefaultHoursPopup(false);
          setCurrentSelectedDefaultHour(undefined);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
        currentDefaultHours={currentSelectedDefaultHour}
        editCourtTypes={selectedDefaultCourtTypes}
      />
      <CreateSeasonalHoursPopup
        showPopup={showCreateSeasonalHoursPopup}
        onClose={() => {
          setShowCreateSeasonalHoursPopup(false);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
      />
      <EditSeasonalHoursPopup
        showPopup={showEditSeasonalHoursPopup}
        onClose={() => {
          setShowEditSeasonalHoursPopup(false);
          setSelectedSeasonalRow(undefined);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
        currentSeasonalHours={selectedSeasonalRow?.sample}
        courtTypes={selectedSeasonalRow?.courtTypes ?? []}
      />
      <CreateSpecialHoursPopup
        showPopup={showCreateSpecialHoursPopup}
        onClose={() => {
          setShowCreateSpecialHoursPopup(false);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
      />
      <EditSpecialHoursPopup
        showPopup={showUpdateSpecialHoursPopup}
        onClose={() => {
          setShowUpdateSpecialHoursPopup(false);
          setSelectedSpecialRow(undefined);
        }}
        onConfirm={() => {
          fetchChosenItems({ fetchFacility: true });
        }}
        currentSpecialHours={selectedSpecialRow?.sample}
        courtTypes={selectedSpecialRow?.courtTypes ?? []}
      />
    </div>
  );
}

export default AdminHandleFacility;
